import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { QuestionPreview } from "../../../stores/CoursePreviewStore/Types";
import { Enrolment } from "../../../stores/EnrolmentStore/Types";
import { displayPercentage } from "../helpers";
import {
  isLessonReachable,
  isUnstartedInteractiveAnswer,
} from "../../../utils/interactiveCourse";

// The lesson pages carry one SVG sprite (see Icons.tsx) and reference it by id,
// rather than shipping an icon component per glyph.
const Icon = ({ id }: { id: string }) => (
  <svg aria-hidden="true">
    <use href={`#${id}`} />
  </svg>
);

interface Props {
  showSidebar: boolean;
  handleHideSidebar: () => void;
  lesson: QuestionPreview;
  enrolment: Enrolment;
  // As CourseSubNav: the figure the page works out for itself, so the drawer
  // and the ring beside it never disagree. See livePercentageCompleted.
  percentage?: number;
  chapters: any;
  answers: any;
  currentUser: any;
}

// The drawer and the course overview timeline are the same object seen twice,
// so the state rules live in one shape in both places. Anything changed here
// almost certainly wants changing in Learning/Chapters too.
const questionPath = (enrolmentId, question, answers) => {
  const answer = answers.find((v) => v.question_id == question?.id);

  if (
    answer &&
    question.kind === "Opinion" &&
    ["pending", "passed"].includes(answer["state"])
  ) {
    return `/enrolments/${enrolmentId}/questions/${question.id}/answers/${answer.id}/votes`;
  } else {
    return `/enrolments/${enrolmentId}/questions/${question.id}/answer`;
  }
};

// In the drawer "current" means the lesson you are looking at, not the one the
// enrolment would send you to next — you can open the drawer from any lesson,
// and marking a different circle would be a lie about where you are.
const questionClass = (
  enrolment,
  question,
  answers,
  currentLessonId,
  chapters = [],
) => {
  const answerIdx = answers.findIndex((v) => v.question_id == question?.id);
  const answer = answers[answerIdx];

  if (enrolment?.locked && !["passed", "failed"].includes(answer?.state)) {
    return "locked";
  } else if (answerIdx == -1 && enrolment?.preview && !question?.preview) {
    return "locked";
  } else if (
    // Interactive courses run in order: the challenges feed each other, so
    // everything past the next unanswered lesson stays shut. Ported from the
    // LessonSidebar this drawer replaced.
    !isLessonReachable(enrolment, chapters, answers, question?.id)
  ) {
    return "locked";
  } else if (question?.id == currentLessonId) {
    return "current";
  } else if (
    // Seeded but not submitted -- nothing is being marked, so show it as
    // untouched rather than as pending.
    isUnstartedInteractiveAnswer(question, answer)
  ) {
    return "not-started";
  } else if (answerIdx == -1) {
    return "not-started";
  } else {
    return answer["state"];
  }
};

const stateLabel = (state) => {
  switch (state) {
    case "current":
      return "Your current lesson";
    case "passed":
      return "Passed";
    case "failed":
      return "Lesson failed";
    case "pending":
    case "submitted":
      return "Submitted, awaiting marking";
    case "locked":
      return "Locked";
    default:
      return "Not started";
  }
};

const renderQuestionState = (state, index, profilePictureUrl) => {
  switch (state) {
    case "current":
      return (
        <span className="lava">
          {profilePictureUrl ? (
            <img src={profilePictureUrl} alt="" loading="lazy" />
          ) : (
            <span className="lnum">{index + 1}</span>
          )}
        </span>
      );
    case "pending":
    case "submitted":
      return <Icon id="i-clock" />;
    case "failed":
      return <Icon id="i-retry" />;
    case "passed":
      return <Icon id="i-check" />;
    case "locked":
      return <Icon id="i-lock" />;
    default:
      return <span className="lnum">{index + 1}</span>;
  }
};

// The drawer is only ~430px wide, so a chapter of seven lessons has to wrap.
// Letting flex-wrap decide leaves a connector dangling off the end of the row
// it broke on, so the rows are cut explicitly and each one ends on a lesson.
const LESSONS_PER_ROW = 4;

const chunk = (items, size) =>
  items.reduce((rows, item, i) => {
    if (i % size === 0) rows.push([]);
    rows[rows.length - 1].push(item);
    return rows;
  }, []);

const translated = (record, translations, locale, field) => {
  const translation = translations?.find((t) => t.language === locale);

  return translation ? translation[field] : record[field];
};

const DrawerLesson = ({
  question,
  i,
  answers,
  enrolment,
  currentLesson,
  currentUser,
  handleHideSidebar,
  chapters,
}) => {
  const state = questionClass(
    enrolment,
    question,
    answers,
    currentLesson?.id,
    chapters,
  );
  const name = translated(
    question,
    question.translations,
    currentUser?.locale,
    "name",
  );
  const minutes = question.length_minutes;

  return (
    <Link
      className={`lesson lesson--${state} ${
        state === "locked" ? "click-disabled" : ""
      }`}
      to={questionPath(enrolment?.id, question, answers)}
      onClick={handleHideSidebar}
      aria-label={`Lesson ${i + 1}: ${name}. ${minutes} minutes. ${stateLabel(
        state,
      )}.`}
      aria-current={state === "current" ? "page" : undefined}
    >
      {renderQuestionState(state, i, enrolment?.user?.profile_picture_url)}
      <span className="tip" aria-hidden="true">
        {name}
        <span className="dur">
          {minutes} minutes
          {state === "pending" || state === "submitted"
            ? " · awaiting marking"
            : ""}
          {state === "failed" ? " · lesson failed" : ""}
          {state === "locked" ? " · locked" : ""}
        </span>
      </span>
    </Link>
  );
};

const ChapterDrawer: React.FC<Props> = ({
  showSidebar,
  handleHideSidebar,
  lesson,
  enrolment,
  percentage: livePercentage,
  chapters,
  answers,
  currentUser,
}) => {
  // Escape closes it, and the page behind must not scroll under the open
  // drawer — on a phone that is the difference between a drawer and a trapdoor.
  useEffect(() => {
    if (!showSidebar) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleHideSidebar();
    };

    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [showSidebar, handleHideSidebar]);

  const percentage = livePercentage ?? displayPercentage(enrolment);
  // r=24 → circumference 2πr = 150.80.
  const CIRCUMFERENCE = 150.8;

  return (
    <>
      <div
        className={`chapter-scrim ${showSidebar ? "is-open" : ""}`}
        onClick={handleHideSidebar}
        aria-hidden="true"
      />
      <aside
        className={`chapter-drawer ${showSidebar ? "is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Chapters and lessons"
        aria-hidden={!showSidebar}
      >
        <div className="chapter-drawer__head">
          <div
            className="dl-ring"
            role="img"
            aria-label={`${percentage} percent of this course complete`}
          >
            <svg width="54" height="54" viewBox="0 0 54 54" aria-hidden="true">
              <circle className="track" cx="27" cy="27" r="24" />
              <circle
                className="meter"
                cx="27"
                cy="27"
                r="24"
                strokeDasharray={CIRCUMFERENCE}
                strokeDashoffset={
                  CIRCUMFERENCE - (CIRCUMFERENCE * percentage) / 100
                }
              />
            </svg>
            <span className="pct" aria-hidden="true">
              {percentage}%
            </span>
          </div>
          <div className="ttl">
            <h2 className="chapter-drawer__name">{enrolment?.course?.name}</h2>
            <Link
              className="chapter-drawer__home"
              to={`/enrolments/${enrolment?.id}`}
              onClick={handleHideSidebar}
            >
              Course <Icon id="i-home" />
            </Link>
          </div>
          <button
            type="button"
            className="drawer-close"
            onClick={handleHideSidebar}
            aria-label="Close course menu"
          >
            <Icon id="i-x" />
          </button>
        </div>

        <nav className="chapter-drawer__chapters" aria-label="Chapters">
          {chapters.map((chapter, chapIdx) => (
            <div
              className={`ct-chapter ${chapter.completed ? "is-complete" : ""}`}
              key={chapter.id || chapIdx}
            >
              <div className="ct-node">
                {String(chapIdx + 1).padStart(2, "0")}
                {chapter.completed && (
                  <span className="ct-badge" aria-hidden="true">
                    <Icon id="i-check" />
                  </span>
                )}
              </div>
              <div className="ct-title">
                {translated(
                  chapter,
                  chapter.topic_translations,
                  currentUser?.locale,
                  "name",
                )}
              </div>
              {chunk(chapter.questions, LESSONS_PER_ROW).map((row, rowIdx) => (
                <div className="ct-pips" key={rowIdx}>
                  {row.map((question, i) => (
                    <React.Fragment key={question.id || i}>
                      {i > 0 && <span className="lconn" aria-hidden="true" />}
                      <DrawerLesson
                        question={question}
                        i={rowIdx * LESSONS_PER_ROW + i}
                        answers={answers}
                        enrolment={enrolment}
                        currentLesson={lesson}
                        currentUser={currentUser}
                        handleHideSidebar={handleHideSidebar}
                        chapters={chapters}
                      />
                    </React.Fragment>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
};

export default ChapterDrawer;
